/**
 * Support-reference extraction (ALL-4857, D636).
 *
 * D636 makes one support reference per request the platform norm: the
 * gateway returns it as the `X-Alloy-Support-Reference` header on every
 * response, every RFC 9457 ProblemDetail carries `supportReference`, and
 * mutating success bodies carry it too. This module is the single place the
 * UI reads it from, so every surface agrees on precedence and sanitising.
 *
 * Precedence: the header first, then the body's `supportReference`, then
 * `support_reference`. The snake_case name is what AlloyProblem in
 * alloy-error-core serializes today, so service ProblemDetails already carry
 * a reference; the camelCase name is what the auth layer's BoundedAuthError
 * and D636 use. The client's own `x-alloy-correlation-id` is never used as a support
 * reference, because D636 says the gateway never trusts a client-supplied
 * value as the reference; showing one would send support after an id the
 * server never recorded.
 *
 * Capability: only a 403 body names the missing capability. The field is
 * read as `capability`, then `requiredScope`, then `required_scope`. The
 * Spring branch claude/browser-error-redirect does not add a capability
 * field to its 403 body yet: BoundedAuthError has reasonCode, httpStatus,
 * message and supportReference only. The gateway's own model names the
 * value `requiredScope`, in GatewayCapability and the route requirement,
 * so that name is accepted, and `capability` is accepted first as the D636
 * wording.
 *
 * Both values are display-only text. They are returned as plain strings for
 * the caller to render as text, never as HTML. Control and bidi-override
 * characters are stripped, and length is capped: a reference longer than
 * SUPPORT_REFERENCE_MAX_LENGTH is treated as absent, because a truncated
 * reference would send support after the wrong request; a capability longer
 * than CAPABILITY_MAX_LENGTH is truncated with an ellipsis.
 *
 * Framework-agnostic, with no React or DOM use, so scripts/sync-error-page.mjs
 * copies it verbatim into sandbox-shell and as plain JS into
 * enterprise-wallet and admin-portal.
 */

export const SUPPORT_REFERENCE_HEADER = "X-Alloy-Support-Reference";
export const SUPPORT_REFERENCE_MAX_LENGTH = 128;
export const CAPABILITY_MAX_LENGTH = 96;
export const CAPABILITY_FIELDS = ["capability", "requiredScope", "required_scope"];
export const SUPPORT_REFERENCE_BODY_FIELDS = ["supportReference", "support_reference"];

export interface SupportInfo {
  supportReference: string | null;
  capability: string | null;
}

export interface HeadersLike {
  get(name: string): string | null;
}

export interface ResponseLike {
  status?: number;
  headers?: HeadersLike | null;
}

/** A parsed JSON object body. Named so parameter lists stay comma-free for
 * the plain-JS type stripper in scripts/sync-error-page.mjs. */
export type UnknownRecord = Record<string, unknown>;

export interface SupportInfoInput {
  response?: ResponseLike | null;
  body?: unknown;
}

// C0 and C1 control characters plus the Unicode bidi embedding, override and
// isolate marks, so a value can never reorder or hide surrounding text.
const UNSAFE_CHARACTERS = /[\u0000-\u001F\u007F-\u009F\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

/** Reduces an unknown value to safe display text, or null when it is not a
 * non-empty string. Numbers, objects and arrays are rejected rather than
 * stringified. */
export function toDisplayText(value: unknown, maxLength: number, truncate: boolean): string | null {
  if (typeof value !== "string") return null;
  // Whitespace runs, newlines included, become one space before the
  // remaining control characters are dropped.
  const cleaned = value.replace(/\s+/g, " ").replace(UNSAFE_CHARACTERS, "").trim();
  if (!cleaned) return null;
  if (cleaned.length <= maxLength) return cleaned;
  if (!truncate) return null;
  return `${cleaned.slice(0, maxLength - 1)}\u2026`;
}

function readHeader(response: ResponseLike | null | undefined): string | null {
  if (!response || !response.headers || typeof response.headers.get !== "function") return null;
  try {
    return response.headers.get(SUPPORT_REFERENCE_HEADER);
  } catch {
    return null;
  }
}

function bodyRecord(body: unknown): UnknownRecord | null {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  return Object(body);
}

function statusOf(response: ResponseLike | null | undefined, record: UnknownRecord | null): number | null {
  if (response && typeof response.status === "number") return response.status;
  if (record && typeof record.status === "number") return record.status;
  if (record && typeof record.httpStatus === "number") return record.httpStatus;
  return null;
}

/** Returns the support reference and, for a 403, the missing capability.
 * Accepts a fetch Response, a parsed body, or both. */
export function extractSupportInfo(input: SupportInfoInput): SupportInfo {
  const response = input ? input.response : null;
  const record = bodyRecord(input ? input.body : null);

  const fromHeader = toDisplayText(readHeader(response), SUPPORT_REFERENCE_MAX_LENGTH, false);
  let fromBody = null;
  if (record) {
    for (const field of SUPPORT_REFERENCE_BODY_FIELDS) {
      const value = toDisplayText(record[field], SUPPORT_REFERENCE_MAX_LENGTH, false);
      if (value) {
        fromBody = value;
        break;
      }
    }
  }
  const supportReference = fromHeader || fromBody || null;

  let capability = null;
  if (record && statusOf(response, record) === 403) {
    for (const field of CAPABILITY_FIELDS) {
      const value = toDisplayText(record[field], CAPABILITY_MAX_LENGTH, true);
      if (value) {
        capability = value;
        break;
      }
    }
  }

  return { supportReference, capability };
}

/** Fills the `{capability}` placeholder of a localized template such as
 * "Missing capability: {capability}". */
export function formatMissingCapability(template: string, capability: string): string {
  return template.split("{capability}").join(capability);
}
